import { ReviewEntriesFilterType } from '../enums/reviewEntriesFilterType'
import { ReviewEntriesViewFilter } from '../enums/reviewEntriesViewFilter'
import type { ReviewEntriesAmountRangeFilter, ReviewEntriesFiltersState } from '../types/reviewEntriesFilters'
import type { ReviewEntryTableRow } from '../types/reviewEntryTableRow'
import { isReviewEntriesFilterValueEmpty } from './isReviewEntriesFilterValueEmpty'
import { isReviewEntryProcessingStatus } from './isReviewEntryProcessingStatus'
import { isToValidateStatus } from './isToValidateStatus'
import { getAmountSearchContext, matchesAmountSearch } from './matchesAmountSearch'

const normalizeSearchValue = (value: string): string => value.trim().toLowerCase()

const normalizeFilterValue = (value: string): string => value.trim().toLowerCase()

const matchesSearchTerm = (value: string | undefined, searchTerm: string): boolean => {
  if (!searchTerm) {
    return true
  }

  if (!value) {
    return false
  }

  return value.toLowerCase().includes(searchTerm)
}

const getDocumentRows = (rows: ReviewEntryTableRow[]): Map<string, ReviewEntryTableRow[]> => {
  const groups = new Map<string, ReviewEntryTableRow[]>()

  for (const row of rows) {
    const groupRows = groups.get(row.entryId) ?? []
    groupRows.push(row)
    groups.set(row.entryId, groupRows)
  }

  return groups
}

const getDocumentHeaderRow = (groupRows: ReviewEntryTableRow[]): ReviewEntryTableRow | undefined =>
  groupRows.find((row) => row.showDocumentLevelFields)

const doesRowMatchAmountSearch = (
  row: ReviewEntryTableRow,
  searchQuery: string,
  parsedNumber: number,
  isNumericSearch: boolean,
): boolean =>
  matchesAmountSearch(row.amountExclVat, searchQuery, parsedNumber, isNumericSearch) ||
  matchesAmountSearch(row.amountInclVat, searchQuery, parsedNumber, isNumericSearch) ||
  matchesAmountSearch(row.vatAmount, searchQuery, parsedNumber, isNumericSearch)

const doesDocumentGroupMatchSearch = (groupRows: ReviewEntryTableRow[], searchQuery: string): boolean => {
  const searchTerm = normalizeSearchValue(searchQuery)

  if (!searchTerm) {
    return true
  }

  const { isNumericSearch, parsedNumber } = getAmountSearchContext(searchQuery)
  const headerRow = getDocumentHeaderRow(groupRows)

  if (headerRow && matchesSearchTerm(headerRow.date, searchTerm)) {
    return true
  }

  if (headerRow && matchesSearchTerm(headerRow.supplierName, searchTerm)) {
    return true
  }

  return groupRows.some(
    (row) =>
      matchesSearchTerm(row.account, searchTerm) ||
      matchesSearchTerm(row.description, searchTerm) ||
      matchesSearchTerm(row.vatRate, searchTerm) ||
      doesRowMatchAmountSearch(row, searchQuery, parsedNumber, isNumericSearch),
  )
}

const doesDocumentGroupMatchViewFilter = (
  groupRows: ReviewEntryTableRow[],
  viewFilter: ReviewEntriesViewFilter,
): boolean => {
  if (viewFilter === ReviewEntriesViewFilter.All) {
    return true
  }

  const headerRow = getDocumentHeaderRow(groupRows)

  if (isReviewEntryProcessingStatus(headerRow?.status)) {
    return true
  }

  return isToValidateStatus(headerRow?.status)
}

const amountMatchesRange = (amount: number | undefined, range: ReviewEntriesAmountRangeFilter): boolean =>
  amount != null && (range.from === undefined || amount >= range.from) && (range.to === undefined || amount <= range.to)

const doesDocumentGroupMatchSupplierFilter = (
  groupRows: ReviewEntryTableRow[],
  selectedSuppliers: string[],
): boolean => {
  const normalizedSelectedSuppliers = new Set(selectedSuppliers.map(normalizeFilterValue))
  const headerRow = getDocumentHeaderRow(groupRows)

  if (!headerRow?.supplierName) {
    return false
  }

  return normalizedSelectedSuppliers.has(normalizeFilterValue(headerRow.supplierName))
}

const doesDocumentGroupMatchAccountFilter = (groupRows: ReviewEntryTableRow[], selectedAccounts: string[]): boolean => {
  const normalizedSelectedAccounts = new Set(selectedAccounts.map(normalizeFilterValue))

  return groupRows.some((row) => row.account && normalizedSelectedAccounts.has(normalizeFilterValue(row.account)))
}

const doesDocumentGroupMatchVatRateFilter = (groupRows: ReviewEntryTableRow[], selectedVatRates: string[]): boolean => {
  const normalizedSelectedVatRates = new Set(selectedVatRates.map(normalizeFilterValue))

  return groupRows.some((row) => row.vatRate && normalizedSelectedVatRates.has(normalizeFilterValue(row.vatRate)))
}

const doesDocumentGroupMatchAmountRangeFilter = (
  groupRows: ReviewEntryTableRow[],
  range: ReviewEntriesAmountRangeFilter,
  amountAccessor: (row: ReviewEntryTableRow) => number | undefined,
): boolean => groupRows.some((row) => amountMatchesRange(amountAccessor(row), range))

const doesDocumentGroupMatchAdvancedFilter = (
  groupRows: ReviewEntryTableRow[],
  type: ReviewEntriesFilterType,
  filters: ReviewEntriesFiltersState,
): boolean => {
  switch (type) {
    case ReviewEntriesFilterType.Supplier:
      return doesDocumentGroupMatchSupplierFilter(groupRows, filters.supplier ?? [])
    case ReviewEntriesFilterType.Account:
      return doesDocumentGroupMatchAccountFilter(groupRows, filters.account ?? [])
    case ReviewEntriesFilterType.VatRate:
      return doesDocumentGroupMatchVatRateFilter(groupRows, filters.vatRate ?? [])
    case ReviewEntriesFilterType.ExclVat:
      return doesDocumentGroupMatchAmountRangeFilter(groupRows, filters.exclVat ?? {}, (row) => row.amountExclVat)
    case ReviewEntriesFilterType.InclVat:
      return doesDocumentGroupMatchAmountRangeFilter(groupRows, filters.inclVat ?? {}, (row) => row.amountInclVat)
    default:
      return true
  }
}

const doesDocumentGroupMatchAdvancedFilters = (
  groupRows: ReviewEntryTableRow[],
  advancedFilters: ReviewEntriesFiltersState,
): boolean => {
  const activeFilterTypes = Object.values(ReviewEntriesFilterType).filter(
    (type) => !isReviewEntriesFilterValueEmpty(type, advancedFilters[type]),
  )

  if (activeFilterTypes.length === 0) {
    return true
  }

  return activeFilterTypes.every((type) => doesDocumentGroupMatchAdvancedFilter(groupRows, type, advancedFilters))
}

export const countToValidateDocuments = (rows: ReviewEntryTableRow[]): number => {
  const groups = getDocumentRows(rows)
  let count = 0

  for (const groupRows of groups.values()) {
    const headerRow = getDocumentHeaderRow(groupRows)

    if (isToValidateStatus(headerRow?.status)) {
      count++
    }
  }

  return count
}

export const filterReviewEntriesTableRows = (
  rows: ReviewEntryTableRow[],
  searchQuery: string,
  viewFilter: ReviewEntriesViewFilter,
  advancedFilters: ReviewEntriesFiltersState = {},
): ReviewEntryTableRow[] => {
  const groups = getDocumentRows(rows)
  const filteredRows: ReviewEntryTableRow[] = []

  for (const groupRows of groups.values()) {
    if (!doesDocumentGroupMatchViewFilter(groupRows, viewFilter)) {
      continue
    }

    if (!doesDocumentGroupMatchSearch(groupRows, searchQuery)) {
      continue
    }

    if (!doesDocumentGroupMatchAdvancedFilters(groupRows, advancedFilters)) {
      continue
    }

    filteredRows.push(...groupRows)
  }

  return filteredRows
}

export const hasActiveAdvancedFilters = (advancedFilters: ReviewEntriesFiltersState): boolean =>
  Object.values(ReviewEntriesFilterType).some((type) => !isReviewEntriesFilterValueEmpty(type, advancedFilters[type]))
